'use client';

import { __assign } from '../../_virtual/_tslib.js';

/**
 * Draws a rounded rectangle using the current state of the canvas.
 * If you omit the last three params, it will draw a rectangle
 * outline with a 5 pixel border radius
 * @param {CanvasRenderingContext2D} ctx
 * @param {Number} x The top left x coordinate
 * @param {Number} y The top left y coordinate
 * @param {Number} width The width of the rectangle
 * @param {Number} height The height of the rectangle
 * @param {Number} [radius = 5] The corner radius; It can also be an object
 *                 to specify different radii for corners
 * @param {Number} [radius.tl = 0] Top left
 * @param {Number} [radius.tr = 0] Top right
 * @param {Number} [radius.br = 0] Bottom right
 * @param {Number} [radius.bl = 0] Bottom left
 */
function roundRect(ctx, x, y, width, height, radius) {
    var borderRadius = { tl: 0, tr: 0, br: 0, bl: 0 };
    if (typeof radius === 'number') {
        borderRadius = { tl: radius, tr: radius, br: radius, bl: radius };
    }
    else {
        borderRadius = __assign(__assign({}, borderRadius), radius);
    }
    ctx.beginPath();
    ctx.moveTo(x + borderRadius.tl, y);
    ctx.lineTo(x + width - borderRadius.tr, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + borderRadius.tr);
    ctx.lineTo(x + width, y + height - borderRadius.br);
    ctx.quadraticCurveTo(x + width, y + height, x + width - borderRadius.br, y + height);
    ctx.lineTo(x + borderRadius.bl, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - borderRadius.bl);
    ctx.lineTo(x, y + borderRadius.tl);
    ctx.quadraticCurveTo(x, y, x + borderRadius.tl, y);
    ctx.closePath();
}
// @description: wrapText wraps HTML canvas text onto a canvas of fixed width
// @param ctx - the context for the canvas we want to wrap text on
// @param text - the text we want to wrap.
// @param x - the X starting point of the text on the canvas.
// @param y - the Y starting point of the text on the canvas.
// @param maxWidth - the width at which we want line breaks to begin - i.e. the maximum width of the canvas.
// @param lineHeight - the height of each line, so we can space them below each other.
// @returns an array of [ lineText, x, y ] for all lines
function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
    // First, start by splitting all of our text into words, but splitting it into an array split by spaces
    var words = text.split(' ');
    var line = ''; // This will store the text of the current line
    var testLine = ''; // This will store the text when we add a word, to test if it's too long
    var lineArray = []; // This is an array of lines, which the function will return
    var textArray = [];
    var textHeightHasIncreased = 0;
    // Lets iterate over each word
    for (var i = 0; i < words.length; i++) {
        // Create a test line, and measure it..
        testLine += "".concat(words[i], " ");
        var _a = ctx.measureText(testLine), textWidth = _a.width, actualBoundingBoxAscent = _a.actualBoundingBoxAscent, actualBoundingBoxDescent = _a.actualBoundingBoxDescent;
        // If the width of this test line is more than the max width
        if (textWidth > maxWidth && i > 0) {
            // Then the line is finished, push the current line into "lineArray"
            lineArray.push([line, x, y]);
            textArray.push(line);
            // Increase the line height, so a new line is started
            var increasedLineHeight = actualBoundingBoxAscent + actualBoundingBoxDescent + lineHeight;
            y += increasedLineHeight;
            textHeightHasIncreased += increasedLineHeight;
            // Update line and test line to use this word as the first word on the next line
            line = "".concat(words[i], " ");
            testLine = "".concat(words[i], " ");
        }
        else {
            // If the test line is still less than the max width, then add the word to the current line
            line += "".concat(words[i], " ");
        }
        // If we never reach the full max width, then there is only one line.. so push it into the lineArray so we return something
        if (i === words.length - 1) {
            lineArray.push([line, x, y]);
            textArray.push(line);
        }
    }
    var largestTextWidth = Math.max.apply(Math, textArray.map(function (item) { return ctx.measureText(item).width; }));
    return { lineArray: lineArray, textHeightHasIncreased: textHeightHasIncreased, largestTextWidth: largestTextWidth };
}
var drawing = {
    roundRect: roundRect,
    wrapText: wrapText
};

export { drawing as default };
//# sourceMappingURL=drawing.js.map
